import { auth } from "../../lib/firebase";

export interface RequestOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

export class ApiError extends Error {
  statusCode?: number;
  bodyText?: string;
  isNetworkError?: boolean;
  constructor(message: string, statusCode?: number, bodyText?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.bodyText = bodyText;
  }
}

export async function authenticatedFetch(url: string, options: RequestOptions = {}): Promise<any> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('Authentication required. No active Firebase user found.', 401);
  }
  const token = await user.getIdToken();
  if (!token) {
    throw new ApiError('Authentication required. Failed to retrieve ID token.', 401);
  }

  const headers = { ...options.headers };
  headers['Authorization'] = `Bearer ${token}`;

  // Remove any banned frontend headers
  delete headers['x-user-id'];
  delete headers['x-user-email'];
  delete headers['x-user-role'];
  delete headers['x-tenant-id'];

  const timeoutMs = options.timeoutMs ?? 15000;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const fetchOptions: RequestInit = {
      method: options.method || 'GET',
      headers,
      signal: controller.signal
    };

    if (options.body) {
      if (options.body instanceof FormData) {
        fetchOptions.body = options.body;
        // Do not set Content-Type, browser sets boundary
      } else {
        fetchOptions.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        if (!headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    const response = await fetch(url, fetchOptions);
    clearTimeout(id);

    const contentType = response.headers.get('content-type') || '';
    let responseBody: any = null;
    let text = '';
    
    try {
      text = await response.text();
      if (contentType.includes('application/json')) {
        responseBody = JSON.parse(text);
      } else {
        responseBody = text;
      }
    } catch (e) {
      responseBody = text || null;
    }

    if (!response.ok) {
      const errorMsg = (responseBody && typeof responseBody === 'object' && responseBody.error) 
        ? responseBody.error 
        : (typeof responseBody === 'string' && responseBody)
          ? responseBody
          : `HTTP error ${response.status}`;
      throw new ApiError(errorMsg, response.status, text);
    }

    return responseBody;
  } catch (error: any) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      const err = new ApiError('timeout', 408);
      err.name = 'AbortError';
      throw err;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    const netErr = new ApiError(error.message || 'Network connection unavailable');
    netErr.isNetworkError = true;
    throw netErr;
  }
}
